import React from 'react';
import {
  Image,
  ReadMore as DefaultReadMore,
  StarRatings as DefaultStarRatings,
} from '@tangibleplay/core-ui';

import { StyledDisplayReview } from './DisplayReview.styles';

import {
  AvailableThumbDirectionType,
  DisplayReviewProps,
} from './DisplayReview.types';

const DisplayReview = ({
  userName,
  reviewDate,
  reviewTitle,
  readMoreComponent: ReadMore = DefaultReadMore,
  reviewDescriptionProps,
  isVerifiedPurchase,
  verifiedIconSize = 16,
  verifiedIconSrc = 'https://images.playosmo.com/ratings-reviews/checkmark.svg',
  starRatingsComponent: StarRatings = DefaultStarRatings,
  starRatingsProps,
  thumbImageProps,
  reviewVote,
  agreeVoteCount,
  disagreeVoteCount,
  isUserReview,
  userReviewUpvoteCount,
  verifiedPurchaseMsg,
  userReviewHelpfulMsg,
  allReviewHelpfulMsg,
  reportMsg,
  onAgreeClick,
  onDisagreeClick,
  onReportClick,
  componentClassname = 'review',
  customClassnames = '',
  customStyle = '',
}: DisplayReviewProps) => {
  const handleUpvoteReview = (e: React.MouseEvent<HTMLDivElement>) => {
    onAgreeClick(e);
  };

  const handleDownvoteReview = (e: React.MouseEvent<HTMLDivElement>) => {
    onDisagreeClick(e);
  };

  const getReviewVoteRender = (
    direction: AvailableThumbDirectionType = 'up',
  ) => {
    return (
      <div
        className={`${componentClassname}__vote ${componentClassname}__vote--is-${direction}`}
        onClick={(e) =>
          direction === 'up' ? handleUpvoteReview(e) : handleDownvoteReview(e)
        }
      >
        {thumbImageProps && direction in thumbImageProps && (
          <Image
            className={`${componentClassname}__vote-icon`}
            src={
              reviewVote === (direction === 'up')
                ? thumbImageProps[direction]?.active
                : thumbImageProps[direction]?.default
            }
            alt={direction === 'up' ? 'upvote' : 'downvote'}
            style={{ backgroundSize: 'contain' }}
            imageWidth={thumbImageProps[direction]?.width || 20}
            imageHeight={thumbImageProps[direction]?.height || 20}
          />
        )}
        <span className={`${componentClassname}__vote-count`}>
          {direction === 'up' ? agreeVoteCount : disagreeVoteCount}
        </span>
      </div>
    );
  };

  return (
    <StyledDisplayReview
      className={`${componentClassname} ${customClassnames}`}
      data-qa="product-review"
      customStyle={customStyle}
    >
      <div className={`${componentClassname}__header`}>
        <div className={`${componentClassname}__header-content`}>
          <div className={`${componentClassname}__rating`}>
            <StarRatings {...starRatingsProps} />
          </div>
          <div className={`${componentClassname}__user`}>
            <div className={`${componentClassname}__user-name`}>{userName}</div>
            {isVerifiedPurchase && (
              <div className={`${componentClassname}__verified-purchase`}>
                <Image
                  className={`${componentClassname}__verified-icon`}
                  src={verifiedIconSrc}
                  alt="checkmark"
                  style={{ backgroundSize: 'contain' }}
                  imageWidth={verifiedIconSize}
                  imageHeight={verifiedIconSize}
                />
                {verifiedPurchaseMsg}
              </div>
            )}
          </div>
        </div>

        <div className={`${componentClassname}__date`}>{reviewDate}</div>
      </div>

      {reviewTitle && (
        <div
          className={`${componentClassname}__title`}
        >{`"${reviewTitle}"`}</div>
      )}

      <ReadMore {...reviewDescriptionProps} />

      <div className={`${componentClassname}__footer`}>
        {isUserReview ? (
          <p className={`${componentClassname}__user-review-up-vote`}>
            <span
              className={`${componentClassname}__user-review-up-vote-count`}
            >
              {`${userReviewUpvoteCount} `}
            </span>
            {userReviewHelpfulMsg}
          </p>
        ) : (
          <>
            <div
              className={`${componentClassname}__review-helpful-catchphrase`}
            >
              {allReviewHelpfulMsg}
            </div>
            <div className={`${componentClassname}__votes-wrapper`}>
              {getReviewVoteRender('up')}
              {getReviewVoteRender('down')}
              {reportMsg && onReportClick && (
                <div
                  className={`${componentClassname}__report`}
                  onClick={onReportClick}
                >
                  {reportMsg}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </StyledDisplayReview>
  );
};
DisplayReview.displayName = 'DisplayReview';

export default DisplayReview;
